<script setup lang="ts">
import { useFieldValue, useFormValues, useSetFieldTouched } from 'vee-validate';
import {
  EvaluationQuantityUsageDto,
  NumericQuantityDto,
  ReferencingObjectDto,
} from '@/api/generator';
import { computed, ref, toRef } from 'vue';
import { useFormMode } from '@/components/forms/useFormMode';
import GreenBoxHolder from '../GreenBoxHolder.vue';
import VeeCheckbox from '@/components/forms/Checkbox/VeeCheckbox.vue';
import GeometriesForm from './GeometriesForm.vue';
import DefinitionsForm from './DefinitionsForm.vue';
import EvaluationScopeForm from '../EvaluationScopeForm.vue';
import InlineNotification from '@/components/notifications/InlineNotification.vue';
import EntityReferencesModal from '@/components/EntityReferencesModal/EntityReferencesModal.vue';
import { isPredefinedEntity } from '@/utils/helper';

const props = defineProps<{
  name: string;
}>();

type Quantity = NumericQuantityDto;

const mode = useFormMode();
const { value: formValues } = useFormValues<Quantity>();
const entityReferences = ref<ReferencingObjectDto[] | null>(null);

const { value: values } = useFieldValue<EvaluationQuantityUsageDto | undefined>(
  toRef(() => props.name),
);
const initialSetupUsage = Boolean(values?.isUsageEnabled);
const touch = useSetFieldTouched(`evaluationUsage.definitions`);

const touchDefinitions = () => {
  touch(true);
};

const isInEditMode = mode.value === 'edit';
const isInAddMode = mode.value === 'add';
const isReferenced = computed(() => formValues.isReferenced ?? false);
const isPredefined = computed(() => isPredefinedEntity(formValues));
const isUsageEnabled = computed(() => formValues.evaluationUsage?.isUsageEnabled ?? false);
const isDisabled = computed(() => !isUsageEnabled.value);

const allowExtend = computed(
  () => (isReferenced.value || isPredefined.value) && isUsageEnabled.value,
);

const isEditable = computed(() => {
  if (isUsageEnabled.value) {
    if (isInAddMode) return true;
    if (isInEditMode && !isPredefined.value) return true;
  }
  return false;
});

const isDeletable = computed(() => {
  if (isUsageEnabled.value) {
    if (isInAddMode) return true;
    if (isInEditMode) {
      if (!isPredefined.value && !isReferenced.value) return true;
      if (isPredefined.value || isReferenced.value) return false;
    }
  }
  return false;
});

function handleUseInEvaluationsChange(useInEvaluations: boolean) {
  if (values && !useInEvaluations) {
    values.evaluationGeometries = values.evaluationGeometries?.filter((geom) => !!geom.name);
  }
}
</script>

<template>
  <div class="d-flex flex-column ga-6">
    <InlineNotification
      v-if="(isReferenced || isPredefined) && isInEditMode"
      variant="flat"
      :value="{
        type: 'info',
        message: $t('FieldsCanNotBeEditedBecauseOfReferenceOfEntityMsg', {
          entity: $t('Quantity_one'),
        }),
      }"
    >
      <template #append>
        <span
          v-if="formValues.isReferenced"
          class="ml-2 linkButton"
          @click.stop="() => (entityReferences = formValues.isReferencedBy ?? null)"
          >{{ $t('Info') }}</span
        >
        <EntityReferencesModal
          v-if="formValues.isReferenced"
          :references="entityReferences"
          :title="$t('ReferenceingEntities')"
          @dialogOpen="
            (isOpen: boolean) => {
              if (!isOpen) entityReferences = null;
            }
          "
        ></EntityReferencesModal>
      </template>
    </InlineNotification>

    <GreenBoxHolder v-if="mode !== 'details'" :active="!!values?.isUsageEnabled">
      <VeeCheckbox
        :hideDetails="true"
        :noValidationOnCheck="true"
        name="evaluationUsage.isUsageEnabled"
        :label="$t('UseInEvaluation_other')"
        class="checkbox--standalone"
        :disabled="
          mode === 'edit' &&
          initialSetupUsage &&
          (formValues.isReferenced || isPredefinedEntity(formValues))
        "
        @click="touchDefinitions()"
        @change="handleUseInEvaluationsChange($event as boolean)"
      />
    </GreenBoxHolder>

    <template v-if="!values?.isUsageEnabled && mode === 'details'">
      <p class="text-body-1 font-weight-bold text-primary-lighten-2">
        {{ $t('QuantityNotUsedInEvaluationsMsg') }}
      </p>
    </template>

    <div class="d-flex flex-column ga-6">
      <DefinitionsForm
        name="evaluationUsage.definitions"
        :disabled="isDisabled"
        :allowExtend="allowExtend"
      />

      <GeometriesForm
        name="evaluationUsage.evaluationGeometries"
        :disabled="isDisabled"
        :isEditable="isEditable"
        :isDeletable="isDeletable"
        :title="$t('EvaluationGeometry_other')"
        :tooltipText="$t('AddEvaluationGeometry')"
        :deleteTooltipText="$t('DeleteEvaluationGeometry')"
        :disabledTooltipText="$t('DeleteEvaluationGeometryDisabledReasonTooltipMsg')"
      />
      <EvaluationScopeForm
        name="evaluationUsage"
        :disabled="isDisabled"
        :allowExtend="allowExtend"
      />
    </div>
  </div>
</template>
